import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { commafy, decommafy, swal, toEnglishDigits, typeChecker } from "../../../utils/methods";
import { _post } from "../../../utils/network";

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';

const ProductsCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('products'), link: '/products-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const [productCategories, setProductCategories] = useState([]);
	const getProductCategories = async () => {
		let body = {
			productcategory_name: null,
			category_type: null,
			page: 1,
			count: 1000000
		};
		let response = await _post('/productcategories/admin/getAll', body);
		switch(response.code){
			case 200:
				setProductCategories(response.data.productcategories.map(item => {
					return {
						key: item.productcategory_name,
						value: item.productcategory_id
					}
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [userCategories, setUserCategories] = useState([]);
	const getUserCategories = async () => {
		let body = {
			usercategory_name: null,
			page: 1,
			count: 1000000
		};
		let response = await _post('/usercategories/admin/getAll', body);
		switch(response.code){
			case 200:
				setUserCategories(response.data.usercategories.map(item => {
					return {
						key: item.usercategory_name,
						value: item.usercategory_id
					}
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [createButtonIsLoading, setCreateButtonIsLoading] = useState(false);
	const createProduct = async (event) => {
		event.preventDefault();
		if(createButtonIsLoading)
			return 0;
		let title = event.target.productTitle.value.trim();
		if(!typeChecker(title, 'title'))
			return toast.warn(t('title') + ' ' + t('must_have_at_least_3_characters'));
		let is_active = event.target.productIsActive.checked ? 1 : 0;
		let categoryID = event.target.productCategory.value;
		let userCategoryID = event.target.productUserCategory.value || null;
		let description = event.target.productDescription.value.trim();
		if(!typeChecker(description, 'title'))
			return toast.warn(t('description') + ' ' + t('must_have_at_least_3_characters'));
		let image = document.querySelector('#productImage-upload-list img')?.getAttribute('src').trim() || '';
		if(!typeChecker(image, 'image'))
			return toast.warn(t('image') + ' ' + t('cant_be_empty'));
		let price = parseInt(toEnglishDigits(decommafy(event.target.productPrice.value.trim())));
		if(!typeChecker(price, 'number'))
			return toast.warn(t('enter_price_correctly'));
		let body = {
			product_id: id,
			product_name: title,
			is_active,
			productcategory_id: categoryID,
			usercategory_id: userCategoryID,
			product_price: price,
			product_information: {
				description,
				image
			},
			is_promote: 0
		};
		confirmSwal(async () => {
			setCreateButtonIsLoading(true);
			let response = await _post('/products/admin/' + componentType, body);
			setCreateButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					if(componentType === 'create')
						event.target.reset();
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	}

	const [deleteButtonIsLoading, setDeleteButtonIsLoading] = useState(false);
	const deleteProduct = async () => {
		if(deleteButtonIsLoading)
			return 0;
		let body = {
			product_id: id
		};
		confirmSwal(async () => {
			setDeleteButtonIsLoading(true);
			let response = await _post('/products/admin/delete', body);
			setDeleteButtonIsLoading(false);
			switch(response.code){
				case 200:
					Navigate("/products-list");
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const [product, setProduct] = useState({});
	const getProduct = async () => {
		let body = {
			product_id: id
		};
		let response = await _post('/products/admin/getOne', body);
		switch(response.code){
			case 200:
				setProduct(response.data.product);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	useEffect(() => {
		const fetchData = async () => {
			await getProductCategories();
			await getUserCategories();
			if(componentType === 'edit')
				getProduct();
		}
		fetchData();
	}, []);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t(componentType)} {t('product')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={createProduct}>
						<div className="row gx-2">
							<FormField fieldKey="productTitle" title={t('title')} value={product?.product_name} rtl />
							<FormField type="toggle" fieldKey="productIsActive" title={t('active')} value={product?.is_active} required />
							<FormField type="select" fieldKey="productCategory" title={t('category')} value={product?.productcategory_id} selectItems={productCategories} required rtl />
							<FormField type="select" fieldKey="productUserCategory" title={t('user_category')} value={product?.usercategory_id} selectItems={userCategories} rtl />
							<FormField type="text" fieldKey="productDescription" title={t('description')} value={product?.product_information?.description} required rtl />
							<FormField type="image" fieldKey="productImage" title={t('image')} value={product?.product_information?.image?.replace('https', 'http')} required tip="200*200" fileMaxSize={2} />
							<FormField type="number" fieldKey="productPrice" title={t('price')} value={commafy(product?.product_price)} required ltr />
						</div>
						<div>
							{componentType === 'create' ?
								<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={createButtonIsLoading}>
									<i className="fa fa-save"></i> {t('create')}
								</LoadingButton>
							:
								<>
									<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={createButtonIsLoading}>
										<i className="fa fa-save"></i> {t('update')}
									</LoadingButton>
									<LoadingButton className="btn btn-danger d-inline-block mt-3" onClick={deleteProduct} type="button" name="delete" isLoading={deleteButtonIsLoading}>
										<i className="fa fa-trash"></i> {t('delete')}
									</LoadingButton>
								</>
							}
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default ProductsCreate;
